const GOOGLE_CLIENT_ID = '216134505980-g4b6klo6kv3ll8ggju45ikktufsil698.apps.googleusercontent.com';
const GOOGLE_SCOPES = 'https://www.googleapis.com/auth/calendar.events';
const GOOGLE_DISCOVERY_DOC = 'https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest';

let tokenClient;
let gapiInited = false;
let gisInited = false;

/**
 * Initialize Google API client
 */
const initializeGapiClient = async () => {
  await window.gapi.client.init({
    discoveryDocs: [GOOGLE_DISCOVERY_DOC],
  });
  gapiInited = true;
};

/**
 * Load gapi script if not already loaded
 */
const loadGapi = () => {
  return new Promise((resolve, reject) => {
    if (window.gapi) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/api.js';
    script.onload = () => {
      window.gapi.load('client', async () => {
        try {
          await initializeGapiClient();
          resolve();
        } catch (error) {
          reject(error);
        }
      });
    };
    script.onerror = reject;
    document.body.appendChild(script);
  });
};

/**
 * Initialize Google Identity Services
 */
const initializeGis = () => {
  if (!window.google) {
    throw new Error('Google Identity Services not loaded');
  }
  
  tokenClient = window.google.accounts.oauth2.initTokenClient({
    client_id: GOOGLE_CLIENT_ID,
    scope: GOOGLE_SCOPES,
    callback: '', // Will be set per request
  });
  gisInited = true;
};

/**
 * Request access token and execute callback
 */
const getAccessToken = (callback) => {
  return new Promise((resolve, reject) => {
    tokenClient.callback = async (response) => {
      if (response.error !== undefined) {
        reject(response);
        return;
      }
      try {
        const result = await callback();
        resolve(result);
      } catch (error) {
        reject(error);
      }
    };

    if (window.gapi.client.getToken() === null) {
      tokenClient.requestAccessToken({ prompt: 'consent' });
    } else {
      tokenClient.requestAccessToken({ prompt: '' });
    }
  });
};

/**
 * Convert task to Google Calendar event format
 */
const taskToGoogleEvent = (task, startDate, durationMins) => {
  const start = new Date(startDate);
  const end = new Date(start.getTime() + durationMins * 60000);

  return {
    summary: task.title,
    description: task.description || `${task.domain} - MedMap Pathfinder Task`,
    start: {
      dateTime: start.toISOString(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    end: {
      dateTime: end.toISOString(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    colorId: '9', // Blue color
    source: {
      title: 'MedMap Pathfinder',
      url: window.location.origin,
    },
  };
};

/**
 * Sync tasks to Google Calendar
 */
export const syncTasksToGoogleCalendar = async (tasks, scheduleConfig) => {
  const { scheduleStartHour, scheduleDurationMins, scheduleOffsetDays, isAvoidedDay } = scheduleConfig;

  // Initialize if needed
  if (!gapiInited) {
    await loadGapi();
  }
  if (!gisInited) {
    initializeGis();
  }

  // Helper to get next workday
  const getNextWorkday = (date, offset) => {
    const d = new Date(date);
    d.setDate(d.getDate() + offset);
    while (isAvoidedDay(d.getDay())) {
      d.setDate(d.getDate() + 1);
    }
    return d;
  };

  const syncEvents = async () => {
    const now = new Date();
    let currentDate = getNextWorkday(now, scheduleOffsetDays);
    const endWindow = new Date(currentDate);
    endWindow.setDate(endWindow.getDate() + 7);

    const results = [];

    for (const task of tasks) {
      if (currentDate > endWindow) break;

      // Skip avoided days
      while (isAvoidedDay(currentDate.getDay())) {
        currentDate.setDate(currentDate.getDate() + 1);
        if (currentDate > endWindow) break;
      }

      if (currentDate > endWindow) break;

      // Set time
      const start = new Date(currentDate);
      start.setHours(scheduleStartHour, 0, 0, 0);

      // Create event
      const event = taskToGoogleEvent(task, start, scheduleDurationMins);

      try {
        const response = await window.gapi.client.calendar.events.insert({
          calendarId: 'primary',
          resource: event,
        });
        results.push({ success: true, task: task.domain, eventId: response.result.id });
      } catch (error) {
        results.push({ success: false, task: task.domain, error: error.message });
      }

      // Move to next day
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return results;
  };

  return getAccessToken(syncEvents);
};

/**
 * Sign out from Google
 */
export const signOutGoogle = () => {
  const token = window.gapi?.client?.getToken();
  if (token !== null) {
    window.google.accounts.oauth2.revoke(token.access_token);
    window.gapi.client.setToken('');
  }
};
